import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useEndUser } from "@/lib/endUserAuth";
import { endUserAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dumbbell, Users, ClipboardList, Loader2, ChevronRight } from "lucide-react";

// Línea de targets de un item de plan (clínico o S&C).
function targetLine(it) {
  const parts = [];
  if (it.sets) parts.push(`${it.sets} series`);
  if (it.reps) parts.push(`${it.reps} reps`);
  if (it.load_percent_rm) parts.push(`${it.load_percent_rm}% RM`);
  if (it.rpe) parts.push(`RPE ${it.rpe}`);
  if (it.frequency) parts.push(it.frequency);
  return parts.join(" · ");
}

export default function AppHomePage() {
  const { endUser, relationships } = useEndUser();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    endUserAPI.plans()
      .then((res) => setPlans(res.data.plans || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const firstName = endUser?.name?.split(" ")[0] || "";

  return (
    <div className="space-y-4">
      {/* Saludo */}
      <div>
        <h1 className="text-xl font-bold text-foreground">Hola, {firstName}</h1>
        <p className="text-sm text-muted-foreground">Esto es lo tuyo para hoy</p>
      </div>

      {/* Coaches / profesionales vinculados */}
      {relationships.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />Tus profesionales
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {relationships.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.tenant?.name || "Clínica"}</p>
                  <p className="text-xs text-muted-foreground truncate">como {r.subject?.name}</p>
                </div>
                <Badge variant="secondary" className="text-[10px] capitalize">{r.type || "vínculo"}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Planes activos */}
      <div className="space-y-1">
        <div className="flex items-center gap-2 px-1">
          <Dumbbell className="w-4 h-4 text-primary" />
          <h2 className="text-sm font-semibold">Mis planes</h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : plans.length === 0 ? (
          <Card className="shadow-sm">
            <CardContent className="p-8 text-center">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
              <p className="text-sm text-muted-foreground">Aún no tienes planes activos.</p>
            </CardContent>
          </Card>
        ) : (
          plans.map((p) => (
            <Card key={p.id} className="shadow-sm" data-testid="app-plan-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{p.title}</CardTitle>
                {p.tenant?.name && (
                  <p className="text-xs text-muted-foreground">{p.tenant.name}</p>
                )}
              </CardHeader>
              <CardContent className="space-y-2">
                {(p.items || []).slice(0, 6).map((it) => (
                  <div key={it.id} className="rounded-lg bg-muted/30 px-3 py-2">
                    <p className="text-sm font-medium">{it.exercise?.name || "Ejercicio"}</p>
                    <p className="text-xs text-muted-foreground">{targetLine(it) || "—"}</p>
                  </div>
                ))}
                {(p.items || []).length === 0 && (
                  <p className="text-xs text-muted-foreground italic">Sin ejercicios cargados todavía.</p>
                )}
                <Button
                  className="w-full rounded-full mt-1"
                  onClick={() => navigate(`/app/registrar/${p.id}`)}
                  data-testid="app-log-session-btn"
                >
                  Registrar sesión
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
